/**
 * 5 元素灵符（首页展示用，方块化像素画）
 *  - 金 太极金币（带方孔）
 *  - 木 青木树符
 *  - 水 玄水葫芦
 *  - 火 离火瓶
 *  - 土 坤土山岩
 * 每个灵符 64×64 像素，调用者负责缩放
 */

export type ElementToken = "金" | "木" | "水" | "火" | "土";

/** 灵符的色板（简化版，5 个主色） */
export interface TokenPalette {
  /** 主色（主体填充） */
  main: string;
  /** 高光（左上亮面） */
  light: string;
  /** 阴影（右下暗面） */
  dark: string;
  /** 强调色（符文/线） */
  accent: string;
  /** 背景框 */
  frame: string;
}

export const TOKEN_PALETTES: Record<ElementToken, TokenPalette> = {
  金: { main: "#c8a847", light: "#f0d878", dark: "#7a5a18", accent: "#fff0b0", frame: "#3a2a08" },
  木: { main: "#5a8a3a", light: "#7aaa5a", dark: "#2a4a18", accent: "#a8e080", frame: "#0a2008" },
  水: { main: "#3a7ab0", light: "#6aaad8", dark: "#0a3a6a", accent: "#88c8ff", frame: "#040e1a" },
  火: { main: "#cc3a1a", light: "#ee7a4a", dark: "#7a0a08", accent: "#ffaa44", frame: "#1a0604" },
  土: { main: "#7a5a38", light: "#a88858", dark: "#3a2818", accent: "#e0c080", frame: "#140c04" },
};

const W = 64;
const H = 64;

function fillRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string): void {
  if (w <= 0 || h <= 0) return;
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

/** 太极币（金） */
export function drawJinToken(ctx: CanvasRenderingContext2D, p: TokenPalette): void {
  // 红色挂绳
  for (let y = 0; y < 12; y++) fillRect(ctx, 31, y, 2, 1, "#7a1010");
  // 绳结（中国结方块）
  for (let i = 0; i < 4; i++) {
    fillRect(ctx, 28 + i, 10, 2, 2, "#aa1a1a");
    fillRect(ctx, 28 + i, 12, 2, 1, "#7a1010");
  }
  // 币外圈
  const cx = 32, cy = 32, R = 18;
  for (let y = -R; y <= R; y++) {
    for (let x = -R; x <= R; x++) {
      const d2 = x * x + y * y;
      if (d2 <= R * R) {
        const c = (x < -2) ? p.light : (x > 2) ? p.dark : p.main;
        fillRect(ctx, cx + x, cy + y, 1, 1, c);
      }
    }
  }
  // 币内圈（深框）
  const Ri = 13;
  for (let y = -Ri; y <= Ri; y++) {
    for (let x = -Ri; x <= Ri; x++) {
      const d2 = x * x + y * y;
      if (d2 <= Ri * Ri && d2 > (Ri - 2) * (Ri - 2)) {
        fillRect(ctx, cx + x, cy + y, 1, 1, p.dark);
      }
    }
  }
  // 方孔（中心 5×5）
  for (let y = -2; y <= 2; y++) {
    for (let x = -2; x <= 2; x++) {
      fillRect(ctx, cx + x, cy + y, 1, 1, p.frame);
    }
  }
  // 符字（左右小方块）
  for (let dy = -1; dy <= 1; dy++) {
    fillRect(ctx, cx - 7, cy + dy, 2, 1, p.accent);
    fillRect(ctx, cx + 6, cy + dy, 2, 1, p.accent);
  }
  // 下方红流苏
  for (let y = 50; y < 62; y++) {
    const w = y < 54 ? 4 : (y < 58 ? 6 : 4);
    fillRect(ctx, 28 + (6 - w) / 2, y, w, 1, "#aa1a1a");
  }
  // 底边金边
  fillRect(ctx, 0, 62, 64, 2, p.accent);
}

/** 青木树符（木） */
export function drawMuToken(ctx: CanvasRenderingContext2D, p: TokenPalette): void {
  // 树冠（多片叶簇）
  const leaf = [
    { x: 16, y: 4, s: 4 }, { x: 24, y: 2, s: 5 }, { x: 32, y: 0, s: 6 },
    { x: 40, y: 2, s: 5 }, { x: 48, y: 4, s: 4 },
    { x: 10, y: 12, s: 4 }, { x: 50, y: 12, s: 4 },
    { x: 8, y: 20, s: 3 }, { x: 52, y: 20, s: 3 },
  ];
  for (const l of leaf) {
    fillRect(ctx, l.x, l.y, l.s, l.s, p.main);
    fillRect(ctx, l.x, l.y, l.s, 1, p.light);
  }
  // 树冠细节高光
  fillRect(ctx, 18, 6, 2, 2, p.accent);
  fillRect(ctx, 34, 2, 2, 2, p.accent);
  fillRect(ctx, 50, 14, 2, 2, p.accent);
  // 树干（多段拼接）
  for (let y = 22; y < 44; y += 3) {
    fillRect(ctx, 30, y, 4, 3, p.main);
  }
  // 木牌（中央）
  for (let y = 30; y < 44; y++) {
    fillRect(ctx, 24, y, 16, 1, p.dark);
    fillRect(ctx, 24, y, 1, 14, p.accent);
    fillRect(ctx, 39, y, 1, 14, p.accent);
  }
  // 牌内字（横线代替）
  for (let dy = 0; dy < 4; dy++) {
    fillRect(ctx, 27, 32 + dy * 3, 10, 1, p.accent);
  }
  // 底部红绳
  for (let x = 28; x < 36; x++) fillRect(ctx, x, 44, 1, 4, "#aa1a1a");
  fillRect(ctx, 26, 48, 12, 1, "#aa1a1a");
  // 底部云/根
  for (let i = 0; i < 4; i++) {
    fillRect(ctx, 18 + i * 8, 52, 4, 2, p.main);
    fillRect(ctx, 19 + i * 8, 51, 2, 1, p.light);
  }
  fillRect(ctx, 0, 60, 64, 4, p.dark);
}

/** 玄水葫芦（水） */
export function drawShuiToken(ctx: CanvasRenderingContext2D, p: TokenPalette): void {
  // 葫芦外轮廓（自顶向下：盖 + 上肚 + 腰 + 下肚）
  // 盖
  fillRect(ctx, 26, 0, 12, 2, p.dark);
  fillRect(ctx, 28, 2, 8, 1, p.main);
  // 上肚（圆）
  for (let y = 3; y < 24; y++) {
    const w = Math.floor(Math.sqrt(Math.max(0, 60 - (y - 13) ** 2 / 4)));
    fillRect(ctx, 32 - w, y, w * 2, 1, y < 10 ? p.light : p.main);
  }
  // 腰部
  for (let y = 24; y < 30; y++) {
    const w = y < 27 ? 5 : 7;
    fillRect(ctx, 32 - w, y, w * 2, 1, p.main);
  }
  // 下肚（圆，更大）
  for (let y = 30; y < 56; y++) {
    const w = Math.floor(Math.sqrt(Math.max(0, 360 - (y - 43) ** 2 * 1.5)));
    fillRect(ctx, 32 - w, y, w * 2, 1, y < 40 ? p.main : p.dark);
  }
  // 高光
  fillRect(ctx, 22, 14, 2, 12, p.accent);
  fillRect(ctx, 24, 10, 1, 18, p.light);
  // 水波纹
  ctx.fillStyle = p.accent;
  for (let i = 0; i < 3; i++) {
    for (let x = 18; x < 46; x++) {
      const yy = 38 + i * 4 + Math.sin((x + i * 6) * 0.3) * 1;
      fillRect(ctx, x, yy, 1, 1, p.accent);
    }
  }
  // 中央木牌
  fillRect(ctx, 24, 32, 16, 14, p.dark);
  fillRect(ctx, 24, 32, 1, 14, p.accent);
  fillRect(ctx, 39, 32, 1, 14, p.accent);
  // 牌字（横线）
  for (let dy = 0; dy < 4; dy++) fillRect(ctx, 27, 35 + dy * 3, 10, 1, p.accent);
  // 底部尖坠
  fillRect(ctx, 30, 58, 4, 1, p.accent);
  for (let y = 58; y < 64; y++) {
    const w = Math.max(0, 4 - (y - 58));
    fillRect(ctx, 32 - w, y, w * 2, 1, p.accent);
  }
}

/** 离火瓶（火） */
export function drawHuoToken(ctx: CanvasRenderingContext2D, p: TokenPalette): void {
  // 瓶中央（圆形）
  for (let y = 14; y < 50; y++) {
    for (let x = 14; x < 50; x++) {
      const dx = x - 32, dy = y - 32;
      if (dx * dx + dy * dy <= 16 * 16) {
        const c = y < 26 ? p.light : y < 40 ? p.main : p.dark;
        fillRect(ctx, x, y, 1, 1, c);
      }
    }
  }
  // 瓶底座
  fillRect(ctx, 16, 50, 32, 3, p.dark);
  fillRect(ctx, 14, 52, 36, 2, p.accent);
  // 瓶身木牌
  fillRect(ctx, 24, 26, 16, 18, p.dark);
  fillRect(ctx, 24, 26, 1, 18, p.accent);
  fillRect(ctx, 39, 26, 1, 18, p.accent);
  for (let dy = 0; dy < 4; dy++) fillRect(ctx, 27, 30 + dy * 4, 10, 1, p.accent);
  // 火焰包裹瓶身
  const flame = (cx: number, cy: number, len: number, w: number, color: string) => {
    for (let i = 0; i < len; i++) {
      const t = i / len;
      const ww = Math.max(1, Math.floor(w * (1 - t) + 1));
      fillRect(ctx, cx - Math.floor(ww / 2), cy - i, ww, 1, color);
    }
  };
  // 左侧火焰（多层）
  flame(20, 14, 16, 5, p.main);
  flame(20, 14, 12, 3, p.light);
  flame(20, 14, 8, 2, p.accent);
  // 右侧火焰
  flame(44, 14, 16, 5, p.main);
  flame(44, 14, 12, 3, p.light);
  flame(44, 14, 8, 2, p.accent);
  // 顶部火焰
  flame(32, 12, 14, 5, p.main);
  flame(32, 12, 10, 3, p.light);
  flame(32, 12, 6, 2, p.accent);
  // 底部
  fillRect(ctx, 14, 54, 36, 2, p.dark);
  fillRect(ctx, 18, 56, 28, 2, p.accent);
  // 边缘火星
  fillRect(ctx, 12, 22, 2, 2, p.light);
  fillRect(ctx, 50, 26, 2, 2, p.light);
  fillRect(ctx, 14, 40, 2, 2, p.light);
  fillRect(ctx, 48, 38, 2, 2, p.light);
}

/** 坤土山岩符（土） */
export function drawTuToken(ctx: CanvasRenderingContext2D, p: TokenPalette): void {
  // 火焰底层（红橙）
  for (let y = 30; y < 60; y++) {
    const w = Math.floor(Math.sqrt(Math.max(0, 360 - (y - 45) ** 2 * 1.2)));
    fillRect(ctx, 32 - w, y, w * 2, 1, y < 40 ? p.main : p.dark);
  }
  // 山岩主体（不规则尖顶）
  // 左下岩
  for (let y = 36; y < 64; y++) {
    const w = Math.floor(8 + (y - 36) * 0.4);
    fillRect(ctx, 0, y, w, 1, y < 50 ? p.main : p.dark);
    fillRect(ctx, 0, y, 1, 1, p.light);
  }
  // 右下岩
  for (let y = 36; y < 64; y++) {
    const w = Math.floor(8 + (y - 36) * 0.4);
    fillRect(ctx, 64 - w, y, w, 1, y < 50 ? p.main : p.dark);
  }
  // 中央尖峰
  for (let y = 0; y < 50; y++) {
    const w = Math.floor(2 + y * 0.5);
    fillRect(ctx, 32 - w, y, w * 2, 1, y < 15 ? p.light : y < 30 ? p.main : p.dark);
  }
  // 火焰（红色火焰环绕尖峰）
  flameOnStone: {
    for (let dy = 0; dy < 12; dy++) {
      const w = Math.max(1, 4 - Math.floor(dy / 3));
      fillRect(ctx, 32 - w, 50 - dy, w * 2, 1, dy < 4 ? p.light : p.main);
    }
  }
  // 岩石裂缝（高亮）
  fillRect(ctx, 28, 30, 1, 12, p.accent);
  fillRect(ctx, 36, 36, 1, 8, p.accent);
  fillRect(ctx, 14, 56, 1, 6, p.accent);
  fillRect(ctx, 50, 52, 1, 8, p.accent);
  // 中央木牌
  fillRect(ctx, 24, 28, 16, 18, p.dark);
  fillRect(ctx, 24, 28, 1, 18, p.accent);
  fillRect(ctx, 39, 28, 1, 18, p.accent);
  for (let dy = 0; dy < 4; dy++) fillRect(ctx, 27, 32 + dy * 4, 10, 1, p.accent);
  // 底座
  fillRect(ctx, 0, 60, 64, 4, p.accent);
  fillRect(ctx, 0, 62, 64, 2, p.dark);
  // 绳结
  for (let x = 28; x < 36; x++) fillRect(ctx, x, 4, 1, 6, "#aa1a1a");
}

/** 统一入口：按元素名调用对应灵符 */
export function drawElementToken(el: ElementToken, ctx: CanvasRenderingContext2D, p: TokenPalette): void {
  switch (el) {
    case "金": return drawJinToken(ctx, p);
    case "木": return drawMuToken(ctx, p);
    case "水": return drawShuiToken(ctx, p);
    case "火": return drawHuoToken(ctx, p);
    case "土": return drawTuToken(ctx, p);
  }
}
